// dev-cart-drawer — the Yakuru cart drawer (snippets/cart-drawer.liquid +
// snippets/dev-cart-drawer-content.liquid).
//
// A Component, so Horizon's declarative `on:click="#cart-drawer/toggle"` on the
// header cart button reaches it. Always a MODAL dialog: native focus trap,
// Escape, top layer and a backdrop at every width (the design is an overlay,
// not Horizon's page-squeezing sidebar).
//
// All markup comes from the server. Every change posts to the Cart AJAX API
// asking for `cart-drawer-section` back and swaps `#dev-cart-content`, so
// money formatting, selling-plan prices and discounts are Shopify's own.
// Each change is announced with the standard CartLinesUpdateEvent so the
// header bubble, buy-box quantities and anything else listening stay in sync;
// adds made elsewhere (buy boxes, quick add) refresh the drawer and open it.
//
// The /cart page (Daria Figma 2026-10-21, "Edit Your Order") renders the same
// snippet in page mode inside sections/dev-cart-page.liquid. There the
// element carries `data-section` (that section's id) and every change asks for
// THAT section back, so the page gets page markup, never the drawer's. The
// page's own controls — "How many people?", ring size pills — are handled
// below (#onRadio); the drawer has none of them.

import { Component } from '@theme/component';
import { lockScroll, unlockScroll, fetchConfig } from '@theme/utilities';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const SECTION = 'cart-drawer-section';
const SOURCE = 'dev-cart-drawer';
// Drawer and page class names, so one handler serves both markups.
const NAME = '.dev-cart__name, .dev-cart-page__name';
const PRICE = '.dev-cart__price, .dev-cart-page__price';
const QTY_VALUE = '.dev-cart__qty-value, .dev-cart-page__qty-value';
const PLAN_ROW = '.dev-cart__plan, .dev-cart-page__plan';
const TOTAL = '.dev-cart__total-row--total dd, [data-dev-cart-total]';

class DevCartDrawer extends Component {
  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  get panel() {
    return /** @type {HTMLDialogElement} */ (this.querySelector('dialog'));
  }

  /** The /cart page renders this drawer as a page panel (no dialog): always "open". */
  get isPage() {
    return this.dataset.mode === 'page';
  }

  get isOpen() {
    return this.isPage || (this.panel?.open ?? false);
  }

  /** The section asked for after a change: the drawer's, or the cart page's own. */
  get sectionId() {
    return this.dataset.section || SECTION;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    // 'scroll' does not bubble; capture it to keep the rail buttons current.
    this.addEventListener('scroll', this.#onScroll, true);
    window.addEventListener('resize', this.#layout);
    this.panel?.addEventListener('cancel', this.#onCancel);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onExternalUpdate);
    this.#pruneOrphans();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('scroll', this.#onScroll, true);
    window.removeEventListener('resize', this.#layout);
    this.panel?.removeEventListener('cancel', this.#onCancel);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onExternalUpdate);
    if (this.panel) unlockScroll(this.panel);
  }

  /* ---------------- open / close ---------------- */

  open() {
    const { panel } = this;
    if (!panel || panel.open) return;
    this.#previouslyFocused = /** @type {HTMLElement | null} */ (document.activeElement);
    panel.classList.remove('is-closing');
    lockScroll(panel);
    panel.showModal();
    this.setAttribute('open', '');
    this.#layout();
    panel.querySelector('.dev-cart__close')?.focus({ preventScroll: true });
    this.dispatchEvent(new DrawerOpenEvent());
  }

  close() {
    const { panel } = this;
    if (!panel || !panel.open || panel.classList.contains('is-closing')) return;
    const finish = () => {
      panel.classList.remove('is-closing');
      panel.close();
      unlockScroll(panel);
      this.removeAttribute('open');
      this.dispatchEvent(new DrawerCloseEvent());
      this.#previouslyFocused?.focus?.({ preventScroll: true });
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return finish();
    panel.classList.add('is-closing');
    panel.addEventListener('animationend', finish, { once: true });
    // Safety net in case the animation never fires (hidden tab, etc.).
    setTimeout(() => panel.open && panel.classList.contains('is-closing') && finish(), 400);
  }

  toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }

  /** Escape: run the animated close instead of the native instant one. */
  #onCancel = (/** @type {Event} */ event) => {
    event.preventDefault();
    this.close();
  };

  /* ---------------- interactions ---------------- */

  #onClick = (/** @type {MouseEvent} */ event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const { panel } = this;

    // A click on the ::backdrop lands on the <dialog> itself, outside its box.
    if (target === panel) {
      const r = panel.getBoundingClientRect();
      const inside = event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
      if (!inside) this.close();
      return;
    }

    if (target.closest('[data-dev-cart-close]')) return this.close();

    const ringStep = target.closest('[data-dev-cart-ring-step]');
    if (ringStep) {
      const url = ringStep.getAttribute('data-dev-cart-ring-step') || '/';
      this.close();
      const overlay = /** @type {any} */ (window).devFunnelOverlay;
      if (overlay) return overlay.open(url).catch(() => window.location.assign(url));
      return window.location.assign(url);
    }

    const railStep = target.closest('[data-dev-cart-rail]');
    if (railStep) {
      if (railStep.getAttribute('aria-disabled') === 'true') return;
      return this.#scrollRail(Number(railStep.getAttribute('data-dev-cart-rail')));
    }

    // Only the sign-up gummies in the cart: they ship with a paid product, so
    // checkout is an aria-disabled <button> (no link). A click says why and
    // points at the note on the gummies card.
    const giftLocked = target.closest('[data-dev-cart-gift-locked]');
    if (giftLocked) {
      event.preventDefault();
      const note = /** @type {HTMLElement | null} */ (this.querySelector('.dev-cart__gift-lock, .dev-cart-page__gift-lock'));
      if (note) {
        note.classList.remove('is-flash');
        void note.offsetWidth;
        note.classList.add('is-flash');
        note.scrollIntoView({ block: 'nearest', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }
      const why = document.getElementById(giftLocked.getAttribute('aria-describedby') || '')?.textContent?.trim();
      if (why) this.#announce(why);
      return;
    }

    // Page (review fix round 1): while the plan is still owed a free ring,
    // checkout is held back — Shopify would charge that ring in full — and
    // the claim row takes the focus instead.
    const owedGate = this.isPage && target.closest('[data-dev-cart-gate]');
    if (owedGate) {
      const claim = /** @type {HTMLElement | null} */ (this.querySelector('#dev-cart-claim'));
      if (claim) {
        event.preventDefault();
        claim.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        const first = /** @type {HTMLElement | null} */ (claim.querySelector('input:not([disabled])'));
        (first || claim).focus({ preventScroll: true });
        const note = this.querySelector('[data-dev-cart-owed-note]')?.textContent?.trim();
        if (note) this.#announce(note);
        return;
      }
    }

    const qty = target.closest('[data-dev-cart-qty]');
    if (qty) {
      const line = Number(qty.closest('[data-line]')?.getAttribute('data-line'));
      const quantity = Number(qty.getAttribute('data-dev-cart-qty'));
      const title = this.#lineTitle(line);
      // Removing the protocol takes the free rings that came with it, so they
      // do not stay behind at full price.
      const gifts = qty.closest('[data-dev-cart-gift-keys]')?.getAttribute('data-dev-cart-gift-keys');
      if (quantity === 0 && gifts) return this.#removeWithGifts(line, gifts.split(',').filter(Boolean), title);
      const says = quantity === 0 ? () => `${title} removed from cart.` : () => `${title}, quantity ${quantity}. ${this.#linePrice(line)}`;
      return this.#change({ line, quantity }, quantity === 0 ? 'remove' : 'update', says);
    }

    const plan = target.closest('[data-dev-cart-plan]');
    if (plan) {
      const row = plan.closest('[data-line]');
      const line = Number(row?.getAttribute('data-line'));
      const quantity = Number(row?.querySelector(QTY_VALUE)?.textContent || 1);
      const id = plan.getAttribute('data-plan');
      plan.setAttribute('aria-checked', id ? 'true' : 'false');
      plan.closest(PLAN_ROW)?.classList.toggle('is-on', Boolean(id));
      const title = this.#lineTitle(line);
      const says = () => `${title}: ${plan.getAttribute('aria-label')} ${id ? 'on' : 'off'}. ${this.#linePrice(line)}`;
      return this.#change({ line, quantity, selling_plan: id ? Number(id) : null }, 'update', says);
    }

    const add = target.closest('[data-dev-cart-add]');
    if (add) {
      const title = add.closest('li')?.querySelector(NAME)?.textContent?.trim() ?? 'Item';
      return this.#add(Number(add.getAttribute('data-dev-cart-add')), () => `${title} added to cart.`);
    }
  };

  /* ---------------- ADA: announcements, focus, rail buttons ---------------- */

  /** @param {number} line */
  #lineTitle(line) {
    return this.querySelector(`[data-line="${line}"]`)?.querySelector(NAME)?.textContent?.trim() ?? 'Item';
  }

  /** @param {number} line — the price as read aloud ("Sale price $110.00 Regular price $138.00"). */
  #linePrice(line) {
    return (this.querySelector(`[data-line="${line}"]`)?.querySelector(PRICE)?.textContent ?? '').replace(/\s+/g, ' ').trim();
  }

  /** @param {string} message */
  #status(message) {
    const region = this.querySelector('[data-dev-cart-status]');
    if (!region) return;
    // Clear first so the same words twice in a row are still spoken.
    region.textContent = '';
    requestAnimationFrame(() => (region.textContent = message));
  }

  /** @param {number} dir */
  #scrollRail(dir) {
    const rail = /** @type {HTMLElement | null} */ (this.querySelector('.dev-cart__rail'));
    const card = rail?.querySelector('.dev-cart__upsell');
    if (!rail || !card) return;
    const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    rail.scrollBy({ left: dir * (card.getBoundingClientRect().width + gap), behavior: still ? 'auto' : 'smooth' });
  }

  /** Rail buttons: hidden when every card fits, inactive at either end. */
  #syncRail() {
    const rail = /** @type {HTMLElement | null} */ (this.querySelector('.dev-cart__rail'));
    const nav = /** @type {HTMLElement | null} */ (this.querySelector('[data-dev-cart-rail-nav]'));
    if (!rail || !nav) return;
    const max = rail.scrollWidth - rail.clientWidth;
    nav.hidden = max <= 1;
    nav.querySelector('[data-dev-cart-rail="-1"]')?.setAttribute('aria-disabled', String(rail.scrollLeft <= 1));
    nav.querySelector('[data-dev-cart-rail="1"]')?.setAttribute('aria-disabled', String(rail.scrollLeft >= max - 1));
  }

  #onScroll = (/** @type {Event} */ event) => {
    if (/** @type {HTMLElement} */ (event.target).classList?.contains('dev-cart__rail')) this.#syncRail();
  };

  /**
   * WCAG 2.4.11: the sticky totals cover the bottom of the panel, so a control
   * reached with Tab is scrolled clear of them (and of their 48px feather).
   */
  #layout = () => {
    const content = /** @type {HTMLElement | null} */ (this.querySelector('#dev-cart-content'));
    const summary = /** @type {HTMLElement | null} */ (this.querySelector('.dev-cart__summary'));
    if (content) content.style.scrollPaddingBottom = summary ? `${summary.offsetHeight + 56}px` : '';
    this.#syncRail();
  };

  /**
   * @param {{line:number, quantity:number, selling_plan?:number|null}} body
   * @param {'update'|'remove'} action
   * @param {() => string} [says] what to announce once the cart has re-rendered
   */
  async #change(body, action, says) {
    const key = this.querySelector(`[data-line="${body.line}"]`)?.getAttribute('data-key') ?? '';
    await this.#request(Theme.routes.cart_change_url, { ...body }, action, [{ id: key, quantity: body.quantity }], says);
  }

  /** @param {number} line @param {string[]} giftKeys @param {string} title */
  async #removeWithGifts(line, giftKeys, title) {
    const key = this.querySelector(`[data-line="${line}"]`)?.getAttribute('data-key') ?? '';
    /** @type {Record<string, number>} */
    const updates = { [key]: 0 };
    for (const k of giftKeys) updates[k] = 0;
    const lines = Object.keys(updates).map((id) => ({ id, quantity: 0 }));
    // A ring pair (Loom 2026-10-19) is one card for both rings.
    const pair = this.querySelector(`[data-line="${line}"]`)?.hasAttribute('data-dev-cart-pair');
    const says = pair ? `${title} pair removed from cart.` : `${title} and its included items removed from cart.`;
    await this.#request(Theme.routes.cart_update_url, { updates }, 'remove', lines, () => says);
  }

  /** @param {number} id @param {() => string} [says] */
  async #add(id, says) {
    await this.#request(Theme.routes.cart_add_url, { items: [{ id, quantity: 1 }] }, 'add', [{ merchandiseId: String(id), quantity: 1 }], says);
  }

  /**
   * @param {string} url
   * @param {object} payload
   * @param {'add'|'update'|'remove'} action
   * @param {{id?:string, merchandiseId?:string, quantity:number}[]} lines  (adds name the variant, as Horizon's product form does)
   * @param {() => string} [says]
   */
  async #request(url, payload, action, lines, says) {
    const content = this.querySelector('#dev-cart-content');
    content?.setAttribute('aria-busy', 'true');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));

    try {
      const section = this.sectionId;
      const res = await fetch(url, fetchConfig('json', { body: JSON.stringify({ ...payload, sections: section, sections_url: window.location.pathname }) }));
      const data = await res.json();
      if (!res.ok || data.status || data.errors) throw new Error(data.description || data.message || 'Cart update failed');

      // /cart/add.js answers with the added items, not the cart.
      const cart = action === 'add' ? await (await fetch(`${Theme.routes.cart_url}.js`)).json() : data;
      this.#swap(data.sections?.[section]);
      if (says) {
        const total = this.querySelector(TOTAL)?.textContent?.trim();
        this.#status(`${says()}${total ? ` Total ${total}.` : ' Your cart is empty.'}`);
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, source: SOURCE, didError: false },
      });
    } catch (error) {
      deferred.reject(error);
      await this.refresh();
      this.#announce(error instanceof Error ? error.message : 'Cart update failed');
    } finally {
      this.querySelector('#dev-cart-content')?.removeAttribute('aria-busy');
    }
  }

  /** Re-render from the server, e.g. after an add made elsewhere. */
  async refresh() {
    try {
      const section = this.sectionId;
      // The page's section lives in the cart template, so it is asked for at /cart.
      const base = this.isPage ? window.location.pathname : window.Shopify?.routes?.root ?? '/';
      const res = await fetch(`${base}?sections=${encodeURIComponent(section)}`);
      const data = await res.json();
      this.#swap(data[section]);
    } catch (error) {
      console.warn('[dev-cart-drawer] refresh failed', error);
    }
  }

  /** @param {string | undefined} html */
  #swap(html) {
    if (!html) return;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('#dev-cart-content');
    const current = this.querySelector('#dev-cart-content');
    if (!fresh || !current) return;
    // Keep the Explore More rail where the customer left it.
    const railX = current.querySelector('.dev-cart__rail')?.scrollLeft ?? 0;
    const scrollY = current.scrollTop;
    const active = /** @type {HTMLElement | null} */ (document.activeElement);
    const focusedLine = active?.closest?.('[data-line]')?.getAttribute('data-line');
    const focusedQty = active?.getAttribute?.('aria-label');
    // Radios: the page's people and ring size controls (the drawer has none).
    const FOCUSABLE = 'a[href]:not([tabindex="-1"]), button:not([disabled]), select, input:not([disabled])';
    const lineEl = active?.closest?.('[data-line]');
    // Page: a line can move in the cart when its variant changes, so it is
    // found again by product as well as by position.
    const focusedProduct = lineEl?.getAttribute('data-product');
    const focusedIndex = lineEl ? [...lineEl.querySelectorAll(FOCUSABLE)].indexOf(active) : -1;
    const hadFocus = Boolean(active && current.contains(active));
    current.replaceWith(fresh);
    fresh.scrollTop = scrollY;
    this.#pruneOrphans();
    const rail = fresh.querySelector('.dev-cart__rail');
    if (rail) rail.scrollLeft = railX;
    this.#layout();
    // Return focus to the equivalent control after the re-render (WCAG
    // 2.4.3): the same control, else the one in the same place on that line
    // ("Decrease" becomes "Remove" at 1), else the line now in that place
    // (after a removal), else Close.
    if (this.isOpen && hadFocus) {
      const freshLine =
        (focusedProduct && fresh.querySelector(`[data-line][data-product="${focusedProduct}"]`)) ||
        (focusedLine ? fresh.querySelector(`[data-line="${focusedLine}"]`) : null);
      const controls = freshLine ? [...freshLine.querySelectorAll(FOCUSABLE)] : [];
      const again =
        (freshLine && focusedQty && freshLine.querySelector(`[aria-label="${CSS.escape(focusedQty)}"]`)) ||
        (focusedQty && fresh.querySelector(`[aria-label="${CSS.escape(focusedQty)}"]`)) ||
        (focusedIndex >= 0 && controls[Math.min(focusedIndex, controls.length - 1)]) ||
        controls[0] ||
        fresh.querySelector('.dev-cart__close') ||
        (this.isPage && fresh.querySelector(`input[data-dev-cart-size]:checked, ${FOCUSABLE}`));
      /** @type {HTMLElement | null} */ (again)?.focus({ preventScroll: true });
    }
  }

  /* ---------------- cart page: people and ring sizes ---------------- */

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #radioTimer;

  /**
   * Radios move with the arrow keys, which fires `change` at every step: the
   * last choice made within a quarter second is the one sent.
   */
  #onChange = (/** @type {Event} */ event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    if (!input.matches('[data-dev-cart-people], [data-dev-cart-size]')) return;
    clearTimeout(this.#radioTimer);
    this.#radioTimer = setTimeout(() => this.#onRadio(input), 250);
  };

  /** @param {HTMLInputElement} input */
  #onRadio(input) {
    if (!input.isConnected || !input.checked) return;
    if (this.querySelector('#dev-cart-content')?.getAttribute('aria-busy') === 'true') return;
    if (input.matches('[data-dev-cart-people]')) return this.#setPeople(input);
    return this.#setSize(input);
  }

  /** @param {string | undefined} json */
  #props(json) {
    try {
      const value = JSON.parse(json || '{}');
      // Liquid's `properties | json` prints [[name, value], …], not an object.
      if (Array.isArray(value)) return Object.fromEntries(value.filter((pair) => Array.isArray(pair) && pair.length === 2));
      return value && typeof value === 'object' ? value : {};
    } catch (e) {
      return {};
    }
  }

  /** @param {string} url @param {object} body */
  async #post(url, body) {
    const res = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status || data.errors) throw new Error(data.description || data.message || 'Cart update failed');
    return data;
  }

  /**
   * Steps that must land before the one that re-renders (an add before the
   * removal it replaces, so a failure never leaves the cart without the item).
   * @param {Array<() => Promise<unknown>>} steps
   * @param {string} url @param {object} payload @param {'add'|'update'|'remove'} action
   * @param {{id?:string, merchandiseId?:string, quantity:number}[]} lines @param {() => string} [says]
   */
  async #run(steps, url, payload, action, lines, says) {
    const content = this.querySelector('#dev-cart-content');
    content?.setAttribute('aria-busy', 'true');
    try {
      for (const step of steps) await step();
    } catch (error) {
      await this.refresh();
      this.#announce(error instanceof Error ? error.message : 'Cart update failed');
      this.querySelector('#dev-cart-content')?.removeAttribute('aria-busy');
      return;
    }
    await this.#request(url, payload, action, lines, says);
  }

  /** The ring page's pair code (section setting), kept alongside any code already on the cart. */
  async #applyPairCode() {
    const code = this.querySelector('#dev-cart-content')?.getAttribute('data-dev-cart-pair-code');
    if (!code) return;
    const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
    const codes = (cart.discount_codes || []).map((/** @type {{code:string}} */ d) => d.code);
    if (codes.some((c) => c.toLowerCase() === code.toLowerCase())) return;
    codes.push(code);
    await this.#post(Theme.routes.cart_update_url, { discount: codes.join(',') });
  }

  /**
   * "How many people?" on a page line card (snippets/dev-cart-page-line.liquid).
   *   qty   the line quantity (supplements, as their product pages post it)
   *   plan  the protocol's People variant and its own selling plan; the free
   *         bottle and membership follow the count, free rings above it go
   *         (one owed is claimed on the free ring card)
   *   pair  the Ultra Ring: 2 = a pair grouped by `_dev_pair`, as the ring
   *         page adds it; 1 = one ring again
   * @param {HTMLInputElement} input
   */
  #setPeople(input) {
    const card = /** @type {HTMLElement | null} */ (input.closest('[data-line]'));
    if (!card) return;
    const kind = card.dataset.peopleKind;
    const n = Number(input.value);
    const line = Number(card.dataset.line);
    const key = card.dataset.key ?? '';
    const title = this.#lineTitle(line);
    const says = () => `${title}: ${n === 1 ? '1 person' : `${n} people`}. ${this.#linePrice(line)}`;
    const add = (/** @type {object[]} */ items) => () => this.#post(Theme.routes.cart_add_url, { items });

    if (kind === 'qty') return this.#change({ line, quantity: n }, 'update', says);

    if (kind === 'plan') {
      const id = Number(input.dataset.variant);
      const plan = input.dataset.plan;
      /** @type {Record<string, number>} */
      const updates = { [key]: 0 };
      for (const k of (card.dataset.devCartGiftExtras || '').split(',').filter(Boolean)) updates[k] = n;
      let keep = n;
      for (const entry of (card.dataset.devCartRings || '').split(',').filter(Boolean)) {
        const at = entry.lastIndexOf(':');
        const ringKey = entry.slice(0, at);
        const q = Number(entry.slice(at + 1));
        const stay = Math.max(0, Math.min(q, keep));
        keep -= stay;
        if (stay !== q) updates[ringKey] = stay;
      }
      const item = { id, quantity: 1, ...(plan ? { selling_plan: Number(plan) } : {}) };
      const lines = Object.entries(updates).map(([k, q]) => ({ id: k, quantity: q }));
      return this.#run([add([item])], Theme.routes.cart_update_url, { updates }, 'update', lines, says);
    }

    if (kind === 'pair') {
      const isPair = card.hasAttribute('data-dev-cart-pair');
      if (n === 2) {
        if (isPair) return;
        const id = Number(card.dataset.variant);
        const props = this.#props(card.dataset.props);
        const token = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
        const items = [
          { id, quantity: 1, properties: { ...props, 'How many people': '2', _dev_pair: `${token}:1` } },
          { id, quantity: 1, properties: { _dev_pair: `${token}:2` } },
        ];
        const updates = { [key]: 0 };
        return this.#run([() => this.#applyPairCode(), add(items)], Theme.routes.cart_update_url, { updates }, 'update', [{ id: key, quantity: 0 }], says);
      }
      if (!isPair) return this.#change({ line, quantity: 1 }, 'update', says);
      const [k1, k2] = (card.dataset.pairKeys || '').split(',');
      const first = Number(card.dataset.pairFirst || card.dataset.variant);
      const props = this.#props(card.dataset.pairFirstProps);
      delete props._dev_pair;
      if ('How many people' in props) props['How many people'] = '1';
      const item = { id: first, quantity: 1, ...(Object.keys(props).length ? { properties: props } : {}) };
      const updates = { [k1]: 0, [k2]: 0 };
      return this.#run([add([item])], Theme.routes.cart_update_url, { updates }, 'update', [{ id: k1, quantity: 0 }, { id: k2, quantity: 0 }], says);
    }
  }

  /**
   * A ring size pill (snippets/dev-cart-page-sizes.liquid): the line's
   * variant is swapped for the chosen size, keeping its quantity, properties
   * (a free ring stays free, a pair stays a pair) and selling plan. A free
   * ring line holding two rings of one size gives up one of them. A claim row
   * adds the free ring the plan is still owed.
   * @param {HTMLInputElement} input
   */
  #setSize(input) {
    const set = /** @type {HTMLElement | null} */ (input.closest('[data-dev-cart-sizes]'));
    if (!set) return;
    const id = Number(input.value);
    const label = set.querySelector('legend')?.firstChild?.textContent?.trim() || 'Ring size';
    const size = input.closest('label')?.querySelector('.dev-cart-page__pill-value')?.textContent?.trim() ?? '';
    const says = () => `${label} ${size}.`;
    const addUrl = Theme.routes.cart_add_url;

    if (set.hasAttribute('data-claim')) {
      // Review fix round 1: the free bottle and membership the people count is
      // missing (the subscription switched off and on again) come back with
      // the ring, as on the ring-size step.
      const items = [{ id, quantity: 1, properties: { _dev_ring: '1' } }];
      for (const entry of (set.dataset.extras || '').split(',').filter(Boolean)) {
        const [gid, q] = entry.split(':').map(Number);
        if (gid && q > 0) items.push({ id: gid, quantity: q, properties: { _dev_gift: '1' } });
      }
      const lines = items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity }));
      return this.#request(addUrl, { items }, 'add', lines, says);
    }

    const key = set.dataset.key ?? '';
    const qty = Number(set.dataset.qty || 1);
    const plan = set.dataset.plan;
    const props = this.#props(set.dataset.props);
    const extra = { ...(Object.keys(props).length ? { properties: props } : {}), ...(plan ? { selling_plan: Number(plan) } : {}) };
    if (key.startsWith(`${id}:`) || key === String(id)) return;

    if (set.hasAttribute('data-split') && qty > 1) {
      const one = { id, quantity: 1, ...extra };
      return this.#run(
        [() => this.#post(Theme.routes.cart_change_url, { id: key, quantity: qty - 1 })],
        addUrl,
        { items: [one] },
        'add',
        [{ merchandiseId: String(id), quantity: 1 }],
        says
      );
    }
    const item = { id, quantity: qty, ...extra };
    return this.#run([() => this.#post(addUrl, { items: [item] })], Theme.routes.cart_update_url, { updates: { [key]: 0 } }, 'update', [{ id: key, quantity: 0 }], says);
  }

  /**
   * Free gift lines left without the subscription they came with (the
   * snippet lists them in data-dev-cart-orphans) are taken out of the cart.
   */
  #pruneOrphans() {
    const keys = (this.querySelector('#dev-cart-content')?.getAttribute('data-dev-cart-orphans') ?? '').split(',').filter(Boolean);
    // The newsletter gift is one per order: a line merged up to 2+ (signed up
    // twice, re-added) is put back to 1 (data-dev-cart-clamp).
    const clamp = (this.querySelector('[data-dev-cart-clamp]')?.getAttribute('data-dev-cart-clamp') ?? '').split(',').filter(Boolean);
    if ((!keys.length && !clamp.length) || this.#pruning) return;
    this.#pruning = true;
    /** @type {Record<string, number>} */
    const updates = {};
    for (const k of clamp) updates[k] = 1;
    for (const k of keys) updates[k] = 0;
    const lines = Object.keys(updates).map((id) => ({ id, quantity: updates[id] }));
    this.#request(Theme.routes.cart_update_url, { updates }, keys.length ? 'remove' : 'update', lines).finally(() => {
      this.#pruning = false;
    });
  }

  #pruning = false;

  /** @param {string} message */
  #announce(message) {
    const region = this.querySelector('[data-dev-cart-live]');
    if (!region) return;
    region.textContent = '';
    requestAnimationFrame(() => (region.textContent = message));
  }

  /** Adds from buy boxes / quick add: refresh, then open on a successful add. */
  #onExternalUpdate = (/** @type {CartLinesUpdateEvent} */ event) => {
    event.promise
      ?.then(async ({ detail }) => {
        if (detail?.source === SOURCE || detail?.didError) return;
        await this.refresh();
        if (event.action === 'add' && !this.isOpen && !document.querySelector('dialog:modal:not(.dev-cart__dialog)')) this.open();
      })
      .catch(() => {});
  };
}

if (!customElements.get('dev-cart-drawer')) customElements.define('dev-cart-drawer', DevCartDrawer);

// Triggers outside Horizon's header (e.g. the tablet/mobile header's cart
// icon): any element with [data-dev-cart-open] opens the drawer instead of
// navigating to /cart. The href stays as the no-JS fallback.
document.addEventListener('click', (event) => {
  const trigger = /** @type {HTMLElement} */ (event.target).closest?.('[data-dev-cart-open]');
  if (!trigger) return;
  const drawer = /** @type {DevCartDrawer | null} */ (document.getElementById('cart-drawer'));
  if (!drawer || !(drawer instanceof DevCartDrawer)) return;
  event.preventDefault();
  drawer.toggle();
});

// Explore More rail with a mouse (review note: "Horizontal scrolling isn't
// working here"). Touch and trackpads already scroll it natively; a mouse has
// no horizontal wheel and the scrollbar is hidden, so:
//   - a vertical wheel over the rail steps one card left/right while the rail
//     can still move that way (at either end the drawer scrolls as usual);
//   - the rail can be dragged with the mouse.
(() => {
  const RAIL = '.dev-cart__rail';
  let wheelLock = 0;

  const step = (rail) => {
    const card = rail.querySelector('.dev-cart__upsell');
    const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
    return card ? card.getBoundingClientRect().width + gap : rail.clientWidth * 0.8;
  };

  document.addEventListener(
    'wheel',
    (event) => {
      const rail = /** @type {HTMLElement} */ (event.target).closest?.(RAIL);
      if (!rail || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;
      const dir = Math.sign(event.deltaY);
      const max = rail.scrollWidth - rail.clientWidth;
      if ((dir > 0 && rail.scrollLeft >= max - 1) || (dir < 0 && rail.scrollLeft <= 1)) return;
      event.preventDefault();
      const now = Date.now();
      if (now < wheelLock) return;
      wheelLock = now + 350;
      rail.scrollBy({ left: dir * step(rail), behavior: 'smooth' });
    },
    { passive: false }
  );

  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const rail = /** @type {HTMLElement} */ (event.target).closest?.(RAIL);
    if (!rail) return;
    const startX = event.clientX;
    const startLeft = rail.scrollLeft;
    let moved = false;

    const onMove = (e) => {
      // Button released where no pointerup reached us: end the drag.
      if (!(e.buttons & 1)) return onUp();
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) < 5) return;
      if (!moved) {
        moved = true;
        rail.style.scrollSnapType = 'none';
        rail.style.cursor = 'grabbing';
      }
      rail.scrollLeft = startLeft - dx;
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
      if (!moved) return;
      rail.style.cursor = '';
      // Let the snap settle the rail on the nearest card, and swallow the
      // click that ends a drag so it does not follow a card's link.
      rail.style.scrollSnapType = '';
      const swallow = (e) => { e.preventDefault(); e.stopPropagation(); };
      rail.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(() => rail.removeEventListener('click', swallow, { capture: true }), 0);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
  });

  // A press on a card's image or title would otherwise start the browser's
  // own link drag, which cancels the pointer events and leaves the rail
  // stopped between two cards.
  document.addEventListener('dragstart', (event) => {
    if (/** @type {HTMLElement} */ (event.target).closest?.(RAIL)) event.preventDefault();
  });
})();
